import React, { useEffect, useState } from 'react';
import { ShieldExclamationIcon, InformationCircleIcon, CogIcon } from '@heroicons/react/24/outline';

const CancelWarningBanner = ({ project, userType }) => {
  const [remainingDays, setRemainingDays] = useState(0);
  const blockDays = project?.block_days || 90; // 서버에서 받은 차단 기간, 기본값 90일

  useEffect(() => {
    if (project?.status === 'cancelled' && project?.updated_at) {
      const cancelledDate = new Date(project.updated_at);
      const currentDate = new Date();
      const daysSinceCancelled = Math.floor((currentDate - cancelledDate) / (1000 * 60 * 60 * 24));
      setRemainingDays(Math.max(0, blockDays - daysSinceCancelled));
    }
  }, [project, blockDays]);

  if (!project || project.status !== 'cancelled') return null;

  // 고객/VIP 고객용 메시지
  if (userType === 'customer' || userType === 'vip_customer') {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-blue-200 overflow-hidden">
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="h-5 w-5 text-blue-600" />
            <h3 className="text-sm font-bold text-blue-900">프로젝트 종료 안내</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="text-sm text-gray-700 space-y-2">
            <p>고객님의 프로젝트가 취소되었습니다.</p>
            
            {/* 보증서 폐기 안내 */}
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <p className="font-semibold text-red-800 text-sm mb-1">
                ⛔ 중요 안내
              </p>
              <ul className="space-y-1 text-xs text-red-700">
                <li>• 프로젝트 취소로 인해 <span className="font-bold">모든 보증서가 폐기</span>되었습니다</li>
                <li>• 시공 하자보증 및 A/S가 <span className="font-bold">불가</span>합니다</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="font-medium text-gray-800">
                📌 프로젝트 기록: {remainingDays > 0 ? (
                  <>{blockDays}일간 열람 가능 <span className="text-blue-600 font-bold">(잔여: {remainingDays}일)</span></>
                ) : (
                  <span className="text-red-600 font-bold">열람 기간 종료</span>
                )}
              </p>
              <p className="text-gray-600">📞 문의사항: 본사 고객센터 1566-0622</p>
            </div>
            
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 font-medium text-sm">
                ⚠️ 주의사항
              </p>
              <p className="text-yellow-700 text-xs mt-1">
                본사를 통하지 않은 개별 연락이나 직거래 제안을 받으신 경우 즉시 고객센터로 신고해 주시기 바랍니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 소장용 메시지
  if (userType === 'contractor') {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-red-300 overflow-hidden">
        <div className="bg-red-50 border-b border-red-300 px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldExclamationIcon className="h-5 w-5 text-red-600 animate-pulse" />
            <h3 className="text-sm font-bold text-red-900">🚨 계약 해지 - 감시 모드 활성화</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="font-bold text-red-800 text-sm mb-2">⚠️ 절대 금지 사항 (적발 시 즉시 법적 조치)</p>
              <ul className="space-y-1 text-xs text-red-700">
                <li>• 고객 개인정보 무단 사용 → 개인정보보호법 위반</li>
                <li>• 직거래 시도 → 업무방해죄 + 손해배상</li>
                <li>• 본사 사칭 → 사기죄 (5년 이하 징역)</li>
                <li>• 시스템 우회 접속 → 정보통신망법 위반</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-300">
              <p className="font-bold text-yellow-800 text-sm mb-2">[모니터링 현황]</p>
              <ul className="space-y-1 text-xs text-yellow-700">
                <li>✓ 접속 IP: 기록 중</li>
                <li>✓ 활동 로그: 실시간 백업 중</li>
                <li>✓ 고객 접촉 시도: 자동 감지 시스템 작동</li>
              </ul>
            </div>

            <div className="bg-gray-100 p-3 rounded-lg">
              <p className="text-xs text-gray-700">
                ⏰ 데이터 보존: {blockDays}일 <span className="text-red-600 font-bold">(D-{remainingDays})</span>
              </p>
            </div>

            <p className="font-bold text-xs text-red-800 bg-red-100 p-3 rounded-lg border border-red-300 text-center">
              ※ 본 경고 무시 시 민·형사상 책임을 물을 수 있습니다
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 관리자용 메시지
  if (userType === 'admin') {
    const cancelledDate = new Date(project.updated_at);
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-300 overflow-hidden">
        <div className="bg-gray-100 border-b border-gray-300 px-4 py-3">
          <div className="flex items-center gap-2">
            <CogIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-900">⚙️ 관리자 모드 - 취소 프로젝트 관리</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-700 text-sm mb-2">[프로젝트 상태]</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• 상태: 취소 ({cancelledDate.toLocaleDateString('ko-KR')})</li>
                {project.cancel_reason && <li>• 취소 사유: {project.cancel_reason}</li>}
                <li>• 담당 소장: {project.contractor?.name || project.contractor?.company_name || '미배정'}</li>
                <li>• 고객 유형: {project.customer?.user_type === 'vip_customer' ? 'VIP' : '일반'}</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-700 text-sm mb-2">[접근 제한 설정]</p>
              <ul className="space-y-1 text-xs text-blue-600">
                <li>• 고객 접속 차단: {blockDays}일 후 자동 <span className="font-bold">(D-{remainingDays})</span></li>
                <li>• 소장 접속: 무기한 허용 (감시 모드)</li>
                <li>• 데이터 보존: 5년 (법적 증거용)</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="font-semibold text-yellow-700 text-sm mb-2">[관리 옵션]</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 transition-colors">
                  고객 즉시 차단
                </button>
                <button className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 transition-colors">
                  소장 권한 회수
                </button>
                <button className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 transition-colors">
                  법무팀 보고
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CancelWarningBanner;